import Layout from '@components/layout'
import { accountPageModuleQuery } from '@data/sanity/queries/page'
import { SanityAccountPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { useUser } from '@lib/auth'
import { Locale } from '@lib/language'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext, getLinkPageUrl } from '@lib/routes'
import { defaultModuleQueryParameters, getAccountPage } from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type AccountPageProps = BasePageProps & {
  locale: Locale
  page: SanityAccountPage
  site: SanitySiteFragment
}

const AccountPage = ({ draftMode, locale, page, site }: AccountPageProps) => {
  const router = useRouter()
  const { user } = useUser()

  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  // Redirect to login page if not logged in
  useEffect(() => {
    if (user && !user.isLoggedIn) {
      const url = getLinkPageUrl('loginPage')
      router.push(url, url, {
        locale,
      })
    }
  }, [draftMode, locale, router, user])

  if (!user?.isLoggedIn) {
    return null
  }

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={accountPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<AccountPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const accountPage = await getAccountPage(sanityClient, context.locale)

  if (!accountPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(accountPage.page),
      site: getNonNullValues(accountPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export default AccountPage
